import { Alert, AlertColor, CircularProgress } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar/UPSnackbar';
import UPButton from 'components/button';
import { UPTanStackTable } from 'components/tanstack-table';
import WarningBar from 'components/warning-bar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { clearPolicyAction, getTimeRegistrationPolicyAction } from 'store/actions/time-registration-policy-actions';
import {
  policyLoadErrorSelector,
  policyLoadingSelector,
  policyUnitsSelector,
} from 'store/selectors/time-registration-policy-selectors';
import {
  GENERAL__RETRY,
  TIME_REGISTRATION_POLICY__LOAD_ERROR,
  TIME_REGISTRATION_POLICY__SAVED,
} from 'translations/constants';
import { PolicyUnit } from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { buildPolicyColumns, getPolicyRowName, PolicyRow } from './columns';
import PolicyUnitPanel from './PolicyUnitPanel';
import { LoadErrorRow, PolicyContainer, PolicySectionBlock, PolicySectionTitle, SectionNote } from './styles';
import { POLICY_SECTIONS, PolicySection, UNIT_DEFINITIONS } from './units';

interface Toast {
  id: number;
  type: AlertColor;
  content: string;
}

const TimeRegistrationPolicy = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const units = useAppSelector(policyUnitsSelector);
  const loading = useAppSelector(policyLoadingSelector);
  const loadError = useAppSelector(policyLoadErrorSelector);
  const [editingUnit, setEditingUnit] = useState<PolicyUnit | null>(null);
  const toastId = useRef(0);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((type: AlertColor, content: string) => {
    toastId.current += 1;
    const id = toastId.current;
    setToasts(previous => [...previous, { id, type, content }]);
  }, []);
  const dismissToast = (id: number) => setToasts(previous => previous.filter(toast => toast.id !== id));

  const columns = useMemo(() => buildPolicyColumns(t, setEditingUnit), [t]);

  useEffect(() => {
    dispatch(getTimeRegistrationPolicyAction());
    return () => {
      dispatch(clearPolicyAction());
    };
  }, [dispatch]);

  if (loadError) {
    return (
      <PolicyContainer>
        <LoadErrorRow data-testid="tr-policy-load-error">
          <Alert severity="error" variant="filled">
            {t(TIME_REGISTRATION_POLICY__LOAD_ERROR)}
          </Alert>
          <UPButton
            text={t(GENERAL__RETRY)}
            outlined
            onClick={() => dispatch(getTimeRegistrationPolicyAction())}
            testId="tr-policy-load-retry"
          />
        </LoadErrorRow>
      </PolicyContainer>
    );
  }

  if (loading && !units.length) {
    return (
      <PolicyContainer>
        <CircularProgress data-testid="tr-policy-loading" />
      </PolicyContainer>
    );
  }

  const editingState = units.find(u => u.unit === editingUnit);
  const buildSectionRows = (section: PolicySection): PolicyRow[] =>
    UNIT_DEFINITIONS.filter(def => def.section === section)
      .map(def => ({ def, state: units.find(u => u.unit === def.unit) }))
      .filter(row => !!row.state);

  return (
    <PolicyContainer data-testid="tr-policy">
      {POLICY_SECTIONS.map(({ section, titleKey, noteKey }) => (
        <PolicySectionBlock key={section} data-testid={`tr-policy-section-${section}`}>
          <PolicySectionTitle>{t(titleKey)}</PolicySectionTitle>
          <UPTanStackTable
            data={buildSectionRows(section)}
            columns={columns}
            getRowId={getPolicyRowName}
            getRowTestId={row => `tr-policy-row-${getPolicyRowName(row)}`}
            testId={`tr-policy-table-${section}`}
            enableSorting={false}
            showPaginationControls={false}
            initialPageSize={UNIT_DEFINITIONS.length}
            sx={{
              paddingLeft: '16px',
              '& .MuiTableContainer-root': { maxHeight: 'none', overflow: 'visible' },
              '& thead th:last-of-type:hover': { backgroundColor: 'common.white' },
            }}
          />
          {noteKey && (
            <SectionNote>
              <WarningBar message={t(noteKey)} variant="info" />
            </SectionNote>
          )}
        </PolicySectionBlock>
      ))}
      {editingState && (
        <PolicyUnitPanel
          state={editingState}
          onClose={() => setEditingUnit(null)}
          onSaved={name => pushToast('success', t(TIME_REGISTRATION_POLICY__SAVED, { name }))}
        />
      )}
      {toasts.map((toast, index) => (
        <UPSnackBar
          key={toast.id}
          open
          type={toast.type}
          content={toast.content}
          onClose={() => dismissToast(toast.id)}
          vertical="bottom"
          horizontal="right"
          index={index}
          testId={`tr-toast-${toast.id}`}
        />
      ))}
    </PolicyContainer>
  );
};

export default TimeRegistrationPolicy;
